import { ActionContext } from 'vuex';
import { IPages, ISites } from '@llws/lift-entity-interfaces';
import websites from '@/shared/services/websites';
import i18n from '@/i18n';
import {
  IFilterPayload,
  IModifyPayload,
  IBuildingPagePayload,
  IPagePhoto,
  ISelectOption,
  IApiResponse,
  IMetaResponse,
  OrderCommand,
} from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';

interface ISiteData {
  id: number,
  sourceClientId: number
}
interface IWebsiteState {
  list: IApiResponse<ISites[], IMetaResponse> | undefined,
  single: IApiResponse<ISites, null> | undefined,
  filters: any[] | undefined,
  pageSingle: number | undefined,
  siteData: ISiteData | undefined,
  loading: boolean,
}
export default {
  namespaced: true, // 'websites/'
  state: {
    list: undefined,
    single: undefined,
    filters: undefined,
    pages: undefined,
    pageSingle: undefined,
    siteData: undefined,
    loading: true,
  } as IWebsiteState,
  getters: {
    list(state: IWebsiteState) {
      return [...state.list?.data || []];
    },
    loading(state: any): any[] {
      return [...state.loading];
    },
    single(state: any): any {
      return { ...state.single?.data } || null;
    },
    pages(state: any): any[] {
      return [...state.pages?.data || []];
    },
    pageSingle(state: any): any {
      return { ...state.pageSingle?.data } || null;
    },
    pageSinglePhotos(state: any): IPagePhoto[] {
      if (
        state.pageSingle
        && state.pageSingle?.data?.featuredImage
        && typeof state.pageSingle?.data?.featuredImage !== 'string'
      ) {
        return state.pageSingle?.data?.featuredImage.map((v: IPagePhoto, idx: number) => {
          // Assign default fields if they are not present
          if (!Object.keys(v.fields || {}).length) {
            v.fields = {
              en: [
                {
                  label: 'Alt Text',
                  value: '',
                  type: 'text',
                  subtype: 'text-field',
                },
              ],
            };
          }
          return {
            ...v,
            id: (idx + 1),
          };
        });
      }

      return [];
    },
    pageTemplates(state: any) {
      if (state.pages?.data) {
        const templates: ISelectOption[] = [
          { value: '', text: `${i18n.t('website.pages.fields.createTemplate')}` },
        ];

        state.pages.data.forEach((p: IPages) => {
          if (p.template && !templates.some((v) => v.value === p.template)) {
            templates.push({ value: p.template, text: p.template });
          }
        });

        return templates.sort((a, b) => (a.value > b.value ? 1 : -1));
      }

      return [];
    },
    listMeta(state: any) {
      return state.list?.meta || null;
    },
    pageMeta(state: any) {
      return state.pages?.meta || null;
    },
    siteData(state: IWebsiteState) {
      return state.siteData;
    },
  },
  mutations: {
    set(state: any, payload: any) {
      if (Array.isArray(payload.data)) {
        state.list = payload;
      } else {
        state.single = payload;
      }
    },
    reset(state: any) {
      state.list = [];
      state.single = null;
      state.pages = null;
    },
    setFilter(state: any, payload: any) {
      state.filters = payload;
    },
    setPages(state: any, payload: any) {
      if (Array.isArray(payload.data)) {
        state.pages = payload;
      } else {
        state.pageSingle = payload;
      }
    },
    setLoading(state: any, payload: boolean) {
      state.loading = payload;
    },
    setSelectedSiteData(state: IWebsiteState, payload: ISiteData) {
      state.siteData = payload;
    },
  },
  actions: {
    setSelectedSiteData(ctx: ActionContext<any, any>, payload: ISiteData) {
      ctx.commit('setSelectedSiteData', payload);
    },
    async get(
      ctx: ActionContext<any, any>,
      payload: { id?: number, filters?: Record<string, string | number | OrderCommand[]> },
    ) {
      ctx.commit('reset');

      const result = payload!.id
        ? await websites.getById(payload.id)
        : await websites.get(payload.filters);

      ctx.commit('set', result);
    },

    async getPages(ctx: ActionContext<any, any>, payload: any) {
      const result = await websites.getPages(payload.clientKey, payload.websiteId, payload.filters);
      ctx.commit('setPages', result);
    },

    async getBuildingPages(ctx: ActionContext<any, any>, payload: any) {
      const result = await websites.getBuildingPages(payload.clientKey, payload.propertyId, payload.filters);
      ctx.commit('setPages', result);
    },

    async getPageById(ctx: ActionContext<any, any>, payload: IFilterPayload) {
      if (payload.id) {
        const result = await websites.getPageById(payload.parentId!, payload.id);
        ctx.commit('setPages', result);
      }
    },

    async savePage(ctx: ActionContext<any, any>, payload: IBuildingPagePayload) {
      const formattedData = payload.data as IPages;

      if (formattedData.featuredImage && typeof formattedData.featuredImage !== 'string') {
        formattedData.featuredImage = JSON.stringify(formattedData.featuredImage);
      }
      let result;
      if (payload.buildingId) {
        result = await websites.saveBuildingPage(
          payload.id ? 'put' : 'post',
          payload.buildingId!,
          formattedData,
        );
      } else {
        result = await websites.savePage(
          payload.id ? 'put' : 'post',
          payload.parentId!,
          formattedData,
        );
      }
      if ((result as any).translations) {
        ctx.commit('setPages', {
          data: { ...(result as any).translations },
          meta: null,
        });
      } else if (result.data) {
        const stateData = Array.isArray(result.data) ? result.data[0] : result.data;
        ctx.commit('setPages', {
          data: {
            ...stateData,
            ...stateData.featuredImage
            && typeof formattedData.featuredImage === 'string'
            && formattedData.featuredImage.includes('[{')
            && { featuredImage: JSON.parse(stateData.featuredImage) },
          },
          meta: null,
        });

        if (!payload.id) {
          ctx.commit('setPages', {
            data: [...[ctx.getters.pageSingle], ...ctx.getters.pages],
            meta: ctx.getters.pageMeta,
          });
        } else {
          const current = ctx.getters.pages;
          const idx = current.findIndex((p: IPages) => p.id === ctx.getters.pageSingle.id);
          current[idx] = { ...ctx.getters.pageSingle };
          ctx.commit('setPages', {
            data: current,
            meta: ctx.getters.pageMeta,
          });
        }
      }
    },

    async removePage(ctx: ActionContext<any, any>, payload: IPages | IBuildingPagePayload) {
      if (payload.buildingId) {
        await websites.removeBuildingPage(payload.buildingId!, payload.id!);
      } else {
        await websites.removePage((payload as IPages).siteId!, payload as IPages);
      }
      const current = ctx.getters.pages;
      ctx.commit('setPages', current.filter((v: IPages) => v.id !== payload.id));
    },

    async save(ctx: ActionContext<any, any>, payload: IModifyPayload<number, ISites>) {
      const result = await adminRequest.put(`/website-api/sites/${payload.id!}`, payload.data);
      ctx.state.list = {
        ...ctx.state.list,
        data: [...(ctx.state.list.data || []), result.data!],
      };
    },
  },
};
